/**
 * "View as" — stepping into a real account to see what that person sees.
 *
 * A developer cannot judge the teacher experience from an account that holds
 * every permission, and neither can a tester. The screens differ, the scope
 * differs, and the empty states differ most of all: a teacher sees five
 * classes, a parent sees two children, and an administrator sees six hundred
 * students. None of that is visible from the outside.
 *
 * The accounts offered are real people from the demo school, with real
 * classes and real children, because a synthetic one would have the empty
 * states that make a product look broken — the opposite of what either
 * audience is here to assess.
 *
 * Leaving is always one click, and the banner that offers it never goes away.
 * The failure worth designing against is somebody forgetting they are viewing
 * as a student, finding the product impoverished, and reporting that.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError, type ViewAsOptions } from '@/lib/api'
import { useSession } from '@/lib/session'
import { IconChevronDown } from './icons'

export function ViewSwitcher() {
  const { session, can } = useSession()
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<ViewAsOptions | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  // Offered to the two kinds of account that can use it. The API decides for
  // real; this only decides whether to draw the control.
  // Mid-view-as, the permissions on screen are the viewed person's, which
  // would hide this control and force a trip back to switch roles; a session
  // that is viewing as somebody was started by an eligible account.
  const eligible = can('tenants', 'view') || can('feedbackLab', 'create') || session?.impersonatedBy != null

  useEffect(() => {
    if (!open || options) return
    let cancelled = false
    void api
      .viewAsOptions()
      .then((result) => !cancelled && setOptions(result))
      .catch((caught) => !cancelled && setProblem(caught instanceof ApiError ? caught.message : 'Could not load views.'))
    return () => {
      cancelled = true
    }
  }, [open, options])

  // Ready to type the moment the menu opens; a fresh search each time.
  useEffect(() => {
    if (!open) return
    setSearch('')
    searchRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const switchTo = useCallback(async (userId: string | null) => {
    setBusy(true)
    setProblem(null)
    try {
      await api.setViewAs(userId)
      // A full load, not a refetch. Changing who you are changes permissions,
      // scope and navigation; every screen already rendered describes the
      // previous person.
      window.location.assign('/')
    } catch (caught) {
      setProblem(caught instanceof ApiError ? caught.message : 'That view could not be opened.')
      setBusy(false)
    }
  }, [])

  if (!session || !eligible) return null

  const viewing = session.impersonatedBy != null

  // A role matching the search shows all its accounts; otherwise only the
  // accounts whose name or email matches.
  const needle = search.trim().toLowerCase()
  const groups = (options?.roles ?? [])
    .map((group) =>
      !needle || group.label.toLowerCase().includes(needle) || group.role.toLowerCase().includes(needle)
        ? group
        : {
            ...group,
            accounts: group.accounts.filter(
              (account) =>
                account.name.toLowerCase().includes(needle) || account.email.toLowerCase().includes(needle),
            ),
          },
    )
    .filter((group) => group.accounts.length > 0)
  const firstMatch = groups[0]?.accounts[0]

  return (
    <div className="view-as" ref={ref}>
      <button
        className={`view-as__trigger ${viewing ? 'is-active' : ''}`}
        type="button"
        onClick={() => setOpen((shown) => !shown)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="view-as__label">
          <span className="view-as__eyebrow">{viewing ? `Viewing as · ${session.user.roleLabel}` : 'View as'}</span>
          <span className="view-as__name">{viewing ? session.user.displayName : 'Choose a role'}</span>
        </span>
        <IconChevronDown className="view-as__caret" />
      </button>

      {open && (
        <div className="view-as__menu" role="menu">
          {problem && (
            <p className="view-as__problem" role="alert">
              {problem}
            </p>
          )}

          {viewing && (
            <button
              className="view-as__leave"
              type="button"
              disabled={busy}
              onClick={() => void switchTo(null)}
            >
              ← Back to my own account
            </button>
          )}

          <div className="view-as__search">
            <input
              ref={searchRef}
              type="search"
              className="view-as__search-input"
              placeholder="Search user types — teacher, parent, nurse…"
              aria-label="Search user types"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && needle && firstMatch && !busy) void switchTo(firstMatch.id)
              }}
            />
          </div>

          {!options && !problem && <p className="view-as__loading">Loading…</p>}

          {groups.map((group) => (
            <div className="view-as__group" key={group.role}>
              <p className="view-as__group-label">{group.label}</p>
              {/* A role holds hundreds of parents; show a few until a name is typed. */}
              {(needle ? group.accounts.slice(0, 50) : group.accounts.slice(0, 8)).map((account) => (
                <button
                  key={account.id}
                  className="view-as__option"
                  type="button"
                  role="menuitem"
                  disabled={busy}
                  onClick={() => void switchTo(account.id)}
                >
                  {account.name}
                </button>
              ))}
              {group.accounts.length > (needle ? 50 : 8) && (
                <p className="view-as__more">
                  {needle
                    ? `${group.accounts.length - 50} more — keep typing to narrow it down`
                    : `${group.accounts.length - 8} more — type a name to find them`}
                </p>
              )}
            </div>
          ))}

          {options && options.roles.length > 0 && groups.length === 0 && (
            <p className="view-as__loading">No user types match "{search.trim()}".</p>
          )}

          {options && options.roles.length === 0 && (
            <p className="view-as__loading">No accounts available to view as in {options.tenantName}.</p>
          )}

          <p className="view-as__footnote">
            You are in their account and can do anything they can, except change their password. Everything you do is
            recorded against your own account.
          </p>
        </div>
      )}
    </div>
  )
}
